import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontBanner, IStorefrontLink, IStorefrontPage, IStorefrontSection, IStorefrontSettings, IStorefrontTheme } from '../../../models/Storefront';
import './StorefrontEditor.css';
import StorefrontTextEditor from './StorefrontTextEditor';
import StorefrontBannerEditor from './Banners/StorefrontBannerEditor';
import StorefrontCategoryEditor from './StorefrontCategoryEditor';
import StorefrontSearchBarEditor from './StorefrontSearchBarEditor';
import StorefrontAutocompleteStylePanel from './StorefrontAutocompleteStylePanel';
import StorefrontSectionsEditor from './Sections/StorefrontSectionsEditor';
import StorefrontHeaderEditor from './Header/StorefrontHeaderEditor';
import StorefrontFooterEditor from './Footer/StorefrontFooterEditor';
import StorefrontColorPickerButton from './StorefrontColorPickerButton';
import { isDarkColor } from './colorUtils';
import { CommandBar, Icon, ICommandBarItemProps } from '@fluentui/react';
import { IHomePageConfig, INavPod, navPodJustify, navPodScrollJustify, parseHomePageConfig, serializeHomePageConfig } from './homePageConfig';
import { IHeroBannerOptions } from './pagesConfig';
import StorefrontNavPodsEditor, { genId } from './NavPods/StorefrontNavPodsEditor';
import { svgMaskStyle } from './NavPods/NavPodExample';
import StorefrontAffiliationsEditor from './Affiliations/StorefrontAffiliationsEditor';
import RawJsonEditor from './RawJsonEditor';
import { resolvedStyleValue, setSpinner, setStyle, spinnerValue, styleValue } from './styleConfig';
import { ISearchCardStyle, ISearchContainerStyle, ISearchElementStyle, ISearchPriceStyle, ISearchTextStyle } from './searchPageConfig';
import { cardStyleFrom, containerStyleFrom, cssText, EditPencil, elementStyleFrom, fullBleedBandStyle, fullBleedFromColor, priceStyleFrom, textStyleFrom } from './styleControls';
import { stylePartForConfigKey } from './styleBulkApply';
import { resolveHoverPart } from './stylesConfig';
import ProductCardStylePanel from './ProductCardStylePanel';
import { StorefrontCardChromeProvider } from './StorefrontCardChromeContext';
import CategoryPodStylePanel from './CategoryPodStylePanel';
import BrowseAllStylePanel, { BrowseAllBandContent, browseAllBandStyle, browseAllButtonClass } from './BrowseAllStylePanel';
import SpinnerStylePanel from './SpinnerStylePanel';
import { localizerFor, parseLocalizationConfig } from './localizationConfig';
import PreviewCanvas from './PreviewCanvas';
import { IUrlPanelState, useUrlPanelState } from '../../../hooks/useUrlState';

interface IStorefrontHomePageEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    storefrontHeaderLinks: IStorefrontLink[];
    globalHeaderLinks: IStorefrontLink[];
    onHeaderLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontFooterLinks: IStorefrontLink[];
    globalFooterLinks: IStorefrontLink[];
    onFooterLinksChange: (newLinks: IStorefrontLink[]) => void;
    // Full banner list (ALL pages) + setter, owned by the page. The editor filters DISPLAY by the
    // current page but always emits the full list (data-loss guard).
    banners: IStorefrontBanner[];
    onBannersChange: (banners: IStorefrontBanner[]) => void;
    sections: IStorefrontSection[];
    onSectionsChange: (newSections: IStorefrontSection[]) => void;
    // The Home tab editing context: undefined = Main Homepage, else a custom page GUID. On a custom
    // page only banners + sections are editable (main-only sub-editors are hidden).
    selectedPageId?: string;
    // The storefront's custom pages (query-backed, merged with any in-edit dirty edits by the parent).
    pages: IStorefrontPage[];
    // Per-page metadata edits (hero options / banner height) — the parent dirty-tracks these and
    // batch-upserts them on Exit Edit Mode (they are NOT part of the settings save).
    onPageMetadataChange: (page: IStorefrontPage) => void;
}

const StorefrontHomePageEditor: React.FC<IStorefrontHomePageEditorProps> = (props) => {
    // Custom Page editing context. On a custom page, only banners + sections render/edit; the
    // main-home-only sub-editors (title, banner text, search bar, nav pods, category pods,
    // affiliations, and the Home Page Style bar) are hidden. Main homepage stays pixel-identical.
    const isCustomPage = !!props.selectedPageId;
    const currentPage: IStorefrontPage | undefined = isCustomPage
        ? props.pages.find((p) => p.id === props.selectedPageId)
        : undefined;
    // Update the current custom page's metadata (per-page banner height + hero options). The parent
    // dirty-tracks the change and batch-upserts it on Exit Edit Mode.
    const updateCurrentPage = (patch: Partial<IStorefrontPage>) => {
        if (!currentPage) return;
        props.onPageMetadataChange({ ...currentPage, ...patch });
    };

    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fff';
    const textColor = resolvedStyleValue(props.storefrontSettings, 'textColor') || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#000';
    const headerFont = resolvedStyleValue(props.storefrontSettings, 'headerFont') ?? props.globalSettings.headerFont;
    const defaultFont = resolvedStyleValue(props.storefrontSettings, 'defaultFont') || props.globalSettings.defaultFont || undefined;
    const isDark = isDarkColor(backgroundColor);

    // Home page presentation config (homePageConfig). Product-card and category-pod style
    // controls, each in its own right-side panel (mirrors the Search page editor). Strictly
    // additive — an empty config produces no CSS, so the Home page renders exactly as today.
    const homeConfig = parseHomePageConfig(props.storefrontSettings.homePageConfig);
    // Full-bleed bands in the preview (homeConfig.navPodBand / sectionBands). The band background is
    // painted across the whole preview canvas via fullBleedBandStyle; see styleControls / the live
    // HomePage.tsx which does the equivalent 100vw breakout on the storefront.
    const navPodBandStyle = fullBleedBandStyle(homeConfig.navPodBand);
    // Affiliations / endorsements / awards band. Defaults (must match the shopper app): gap 24px,
    // align 'center', position 'bottom', labelPosition 'top' when a label is set else 'none'.
    // Strictly gated on having at least one logo with an imageUrl — an empty/unset config renders
    // nothing, same as nav pods.
    const affiliations = homeConfig.affiliations;
    const affiliationsItems = (affiliations?.items ?? []).filter((i) => !!i.imageUrl);
    const affiliationsVisible = affiliationsItems.length > 0;
    const affiliationsBandStyle = fullBleedBandStyle(affiliations?.band);
    const affiliationsGap = affiliations?.gap ?? 24;
    const affiliationsJustify = navPodJustify(affiliations?.align ?? 'center');
    const affiliationsLabelPosition = affiliations?.labelPosition ?? (affiliations?.label ? 'top' : 'none');
    const affiliationsPodStyle = elementStyleFrom(affiliations?.pod);
    const affiliationsImageStyleSet = elementStyleFrom(affiliations?.image);
    const affiliationsImageStyle: React.CSSProperties = Object.keys(affiliationsImageStyleSet).length
        ? affiliationsImageStyleSet
        : { height: 48, width: 'auto', objectFit: 'contain' };
    const affiliationsLabelStyle = textStyleFrom(affiliations?.labelStyle);
    // Full-bleed banner (mirrors ac-storefront-web HomePage.tsx). Strictly opt-in.
    // Note: homeConfig.fullWidthLayout is intentionally NOT read here. It widens the app shell
    // (Layout.tsx drops #content-inner's 90% gutter) purely so BANDS can bleed to the viewport edge;
    // page CONTENT still stays at maxWidth — HomePage.tsx:1150 wraps the home content in
    // `maxWidth: homeMaxWidth`, and fullBleedBand re-centers each band's inner content at the same
    // cap (constrainInner). Widening the preview's content frames to match the flag is therefore
    // WRONG: it makes sections run edge to edge in the editor while the shopper still sees a
    // centered column. What the flag does need is a band that actually bleeds — see
    // fullBleedBandStyle, which is where a preview band falls short.
    // Hero presentation source: the main homepage reads homePageConfig; a custom page reads its own
    // pagesConfig entry (same field names — see IHeroBannerOptions). Edited from the banner panel.
    const heroOptions: IHeroBannerOptions = isCustomPage ? (currentPage ?? {}) : homeConfig;
    const updateHeroOptions = (patch: Partial<IHeroBannerOptions>) => {
        if (isCustomPage) {
            updateCurrentPage(patch);
        } else {
            props.onStorefrontSettingsChange({ ...props.storefrontSettings, homePageConfig: serializeHomePageConfig({ ...homeConfig, ...patch }) });
        }
    };
    const bannerFullWidth = !!heroOptions.bannerFullWidth;
    // Hero-under-header overlay (bannerUnderHeader): the header floats transparently over the top of
    // the full-bleed hero banner. Only takes effect when bannerFullWidth is also on — the shopper
    // side is likewise gated on full-width (and desktop). Off = today's plain-flow header.
    const bannerUnderHeader = bannerFullWidth && !!heroOptions.bannerUnderHeader;
    // Size the full-bleed hero to the image's natural height rather than cropping it into a
    // fixed-height box. Read off homeConfig (not heroOptions) even on a custom page, because that is
    // where HomePage.tsx reads it from — bannerAutoHeight is not a per-page option on either side.
    const bannerAutoHeight = bannerFullWidth && !!homeConfig.bannerAutoHeight;
    // Scale the banner overlay with the banner instead of locking it to pixels. Same gate as the
    // live page (HomePage.tsx `homeOverlayScale`): full-width only, read off homeConfig.
    const bannerOverlayScale = bannerFullWidth && !!homeConfig.bannerOverlayScale;
    const maxWidthPx = props.storefrontSettings?.maxWidth ?? props.globalSettings?.maxWidth ?? 800;
    // Uniform inter-block spacing (mirrors ac-storefront-web HomePage.tsx). Both strictly opt-in:
    // undefined leaves every margin/padding below exactly as it renders today.
    const homeContentPaddingTop = homeConfig.homeContentPaddingTop;
    const homeBlockGap = homeConfig.homeBlockGap;
    // Store title (h1) styling (homeConfig.pageTitle) — mirrors ac-storefront-web HomePage.tsx, which
    // applies textStyleFrom(homeConfig.pageTitle) to the same h1 (including display:none to hide it).
    const pageTitleStyle = textStyleFrom(homeConfig.pageTitle);
    // In edit mode, don't let display:none hide the editable title field (mirrors showTitleTextOverride
    // in the header editor keeping overridden content editable in edit mode); faithful in preview.
    const pageTitlePreviewStyle: React.CSSProperties =
        props.editMode && pageTitleStyle.display === 'none'
            ? { ...pageTitleStyle, display: undefined }
            : pageTitleStyle;
    // 'card' covers the whole product card — tile, name, price and image are edited together in the
    // one ProductCardStylePanel (they used to be split across a 'card' Pivot panel and an 'image' one).
    /*
     * The style panels on this toolbar are URL-driven, so a link to a field inside one can open
     * it. They share a single openPanel value (only one is ever up), which is derived from whichever
     * panel key is in the URL — that keeps every existing call site working unchanged.
     *
     * That single `?panel=` slot is also what makes the entries mutually exclusive: these panels are
     * non-blocking and anchored to the same edge, so two open at once simply overlap and the bar
     * highlights both. Banners and Sections own their own panels (they hold the state the panel
     * edits) and used to sit OUTSIDE this, each on a private boolean — which is why opening Product
     * Cards left Banners open and lit. They have a key here now: the bar claims the slot and then
     * asks the owner to open, the owner closes itself when `panelActive` says the slot went
     * elsewhere, and it reports canvas-initiated opens back so the slot stays honest either way.
     */
    const stylePanels = {
        sections: useUrlPanelState('sections'),
        banners: useUrlPanelState('banners'),
        card: useUrlPanelState('productCards'),
        pod: useUrlPanelState('categoryPods'),
        spinner: useUrlPanelState('loadingSpinner'),
        browseAllBand: useUrlPanelState('browseAll'),
        suggestions: useUrlPanelState('searchSuggestions'),
    };
    type StylePanelKey = keyof typeof stylePanels;
    const openPanel: StylePanelKey | null =
        (Object.keys(stylePanels) as StylePanelKey[]).find((k) => stylePanels[k].isOpen) ?? null;
    const setOpenPanel = (key: StylePanelKey | null) => {
        if (key) stylePanels[key].open();
        else if (openPanel) stylePanels[openPanel].close();
    };
    // A child-owned panel reporting its own open/close — it may have been opened from the CANVAS
    // rather than the bar. Mirroring that into the URL keeps the one slot authoritative, and makes
    // those opens deep-linkable like every other panel.
    const reportChildPanel = (panel: IUrlPanelState) => (open: boolean) => {
        if (open && !panel.isOpen) panel.open();
        else if (!open && panel.isOpen) panel.close();
    };
    /*
     * Nav pods and affiliations open from the toolbar, which used to be local state — so a link to a
     * field inside either panel landed on the editor with the panel shut and honestly reported it
     * could not find the field. Both are URL-driven now (?panel=navPods / ?panel=affiliations), the
     * same treatment the banner panel already had.
     */
    const navPodsPanel = useUrlPanelState('navPods');
    const navPodsOpen = navPodsPanel.isOpen;
    // One-shot target for the nav pods panel: set when a pod is clicked in the canvas (or the ghost
    // add tile mints a new one), consumed by the panel to expand + scroll to that pod, then cleared.
    const [navPodFocusId, setNavPodFocusId] = React.useState<string | undefined>(undefined);
    // One-shot tab targets, same deal: the nav pods and affiliations panels each hold their content
    // and their styling, and the canvas says which of the two the merchant just clicked.
    const [navPodsPivot, setNavPodsPivot] = React.useState<'pods' | 'style' | undefined>(undefined);
    const affiliationsPanel = useUrlPanelState('affiliations');
    const affiliationsOpen = affiliationsPanel.isOpen;
    const [affiliationsPivot, setAffiliationsPivot] = React.useState<'logos' | 'style' | undefined>(undefined);
    // The banner edit panel is owned by StorefrontBannerEditor (it has to select the visible slide
    // before opening), so the bar button asks rather than sets: a one-shot request flag the editor
    // consumes and reports handled, plus the open state reported back for the button's highlight.
    // Same shape as initialFocusPodId / onInitialFocusHandled for the nav pods.
    const [bannerPanelRequest, setBannerPanelRequest] = React.useState(false);
    // The Sections panel is owned by StorefrontSectionsEditor — reordering, adding and deleting a
    // section are all mutations of the section list, which that component performs. Same
    // ask-don't-set shape as the banner request above.
    const [sectionsPanelRequest, setSectionsPanelRequest] = React.useState(false);
    const openNavPods = (pivot: 'pods' | 'style') => { setNavPodsPivot(pivot); navPodsPanel.open(); };
    const openAffiliations = (pivot: 'logos' | 'style') => { setAffiliationsPivot(pivot); affiliationsPanel.open(); };
    const closePanel = () => setOpenPanel(null);

    // Full-width banner presentation: when bannerFullWidth is on, the banner block breaks out to the
    // full preview width while page content (title, banner text, pods, sections) stays locked to the
    // page maxWidth via contentFrameStyle. pageMaxWidth mirrors maxWidthPx (same source) and is used
    // by contentFrameStyle / the body's non-full-bleed max width.
    const pageMaxWidth = props.storefrontSettings?.maxWidth ?? props.globalSettings?.maxWidth ?? 800;
    // Re-establishes the page maxWidth on the content that sits above/below a full-width banner
    // (mirrors .storefront-body's own 90%/maxWidth/centering). Applied only when bannerFullWidth
    // is on — the wrapper divs are always present, so toggling only restyles (no remount).
    const contentFrameStyle: React.CSSProperties = { width: '90%', maxWidth: `${pageMaxWidth}px`, margin: '0 auto', padding: '0 1em', boxSizing: 'border-box' };

    const updateConfig = (patch: Partial<IHomePageConfig>) => {
        const next = { ...homeConfig, ...patch };
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, homePageConfig: serializeHomePageConfig(next) });
    };
    const updateCard = (patch: Partial<ISearchCardStyle>) => updateConfig({ card: { ...homeConfig.card, ...patch } });
    const updateTitle = (patch: Partial<ISearchTextStyle>) => updateConfig({ title: { ...homeConfig.title, ...patch } });
    const updatePrice = (patch: Partial<ISearchPriceStyle>) => updateConfig({ price: { ...homeConfig.price, ...patch } });
    const updateImage = (patch: Partial<ISearchElementStyle>) => updateConfig({ image: { ...homeConfig.image, ...patch } });
    const updateCardHover = (patch: Partial<ISearchElementStyle>) => updateConfig({ cardHover: { ...homeConfig.cardHover, ...patch } });
    const updatePod = (patch: Partial<ISearchElementStyle>) => updateConfig({ pod: { ...homeConfig.pod, ...patch } });
    const updatePodName = (patch: Partial<ISearchTextStyle>) => updateConfig({ podName: { ...homeConfig.podName, ...patch } });
    const updatePodContainer = (patch: Partial<ISearchContainerStyle>) => updateConfig({ podContainer: { ...homeConfig.podContainer, ...patch } });
    const updatePodHover = (patch: Partial<ISearchElementStyle>) => updateConfig({ podHover: { ...homeConfig.podHover, ...patch } });
    const updateNavPods = (navPods: INavPod[]) => updateConfig({ navPods });
    // Browse All CTA band (AC-969). The band object is edited whole in BrowseAllStylePanel (which
    // owns the merge helpers), since turning the band off has to remove the object outright.
    const bab = homeConfig.browseAllBand;
    // Shopper-facing labels, so the previews read the way the live store does. The live page
    // resolves these through L(...); an unset override falls back to the same defaults the
    // localization editor lists.
    const localization = parseLocalizationConfig(props.storefrontSettings.localizationConfig);
    const browseAllText = localization['localization_home_browse_all'] || 'Browse All';
    const loadingText = localization['localization_common_loading'] || 'Loading';

    // Card hover styling — resolveHoverPart is the renderer's exact pipeline (HomePage.tsx): one
    // ref (the hover dict's own, else the resting card's), that preset's `hover` part under the
    // local cardHover dict. Emitted as a :hover rule below (an inline style can't express :hover);
    // the transition pair mirrors the storefront's so the change animates in the preview too.
    // The floating add-to-cart button on every card. Resolved here, page-level, exactly as
    // HomePage.tsx does it (`homeAddToCartStyle`) — a section has no say over it, so it reaches the
    // cards through StorefrontCardChromeProvider rather than through productStyleProps. It is NOT
    // emitted into homeStyleRules below because the button is a Fluent IconButton whose own styles
    // are injected at runtime; only the `styles` prop reliably beats them (see ProductCard.tsx).
    const homeAddToCartStyle = elementStyleFrom(homeConfig.addToCartButton);
    // Chrome for sections whose `scrollProducts` is on — same page-level resolution the live store
    // does (HomePage.tsx `homeScrollButtonStyle` / `homeScrollbarStyle`).
    const homeScrollButtonStyle = elementStyleFrom(homeConfig.scrollButton);
    const homeScrollbarStyle = elementStyleFrom(homeConfig.scrollbar);
    const homeCardHoverStyle = elementStyleFrom(resolveHoverPart(homeConfig.cardHover, homeConfig.card), 'hover');
    // Section collapse/expand control: the hover part goes through the same resolver as every other
    // hover dict, and the glyphs default to the storefront's own '+' / '−' (U+2212 minus, which
    // optically matches the plus — a hyphen reads lighter).
    const homeSectionToggleHoverStyle = elementStyleFrom(resolveHoverPart(homeConfig.sectionToggleHover, homeConfig.sectionToggle), 'hover');
    const sectionToggleIcons = {
        expand: homeConfig.sectionToggleExpandIcon || '+',
        collapse: homeConfig.sectionToggleCollapseIcon || '−',
    };
    // The per-section "More like this" label. Read through the localizer rather than hardcoded, so a
    // store that renamed it ("More from this sector ›") previews its own wording — the whole point of
    // localizerFor. The default matches the storefront's shipped string.
    const moreLikeThisLabel = localizerFor(props.storefrontSettings?.localizationConfig)('localization_home_more_like_this');
    // Same pipeline for the two pod families (HomePage.tsx emits all three the same way).
    const homePodHoverStyle = elementStyleFrom(resolveHoverPart(homeConfig.podHover, homeConfig.pod), 'hover');
    const homeNavPodHoverStyle = elementStyleFrom(resolveHoverPart(homeConfig.navPodHover, homeConfig.navPod), 'hover');
    // The transition every hover rule pairs with, so the change animates in the preview the way it
    // does on the storefront.
    const hoverTransition: React.CSSProperties = { transition: 'box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease' };
    // Reflect the config in the live Home preview via a scoped stylesheet — this needs no
    // changes to the (deeply nested) section/category preview components. Each rule is
    // emitted only if the merchant set a value, so an empty config yields no rules at all.
    const homeStyleRules: string = ([
        ['.sf-home-style-scope .storefront-home-banner', elementStyleFrom(homeConfig.banner)],
        ['.sf-home-style-scope .storefront-home-message', textStyleFrom(homeConfig.bannerText)],
        ['.sf-home-style-scope .storefront-product-card', cardStyleFrom(homeConfig.card, stylePartForConfigKey('homePageConfig', 'card'))],
        ['.sf-home-style-scope .storefront-product-card-name', textStyleFrom(homeConfig.title, stylePartForConfigKey('homePageConfig', 'title'))],
        ['.sf-home-style-scope .storefront-product-card-price', priceStyleFrom(homeConfig.price, stylePartForConfigKey('homePageConfig', 'price'))],
        ['.sf-home-style-scope .storefront-product-card-image', elementStyleFrom(homeConfig.image, stylePartForConfigKey('homePageConfig', 'image'))],
        ...(Object.keys(homeCardHoverStyle).length ? ([
            ['.sf-home-style-scope .storefront-product-card', hoverTransition],
            ['.sf-home-style-scope .storefront-product-card:hover', homeCardHoverStyle],
        ] as [string, React.CSSProperties][]) : []),
        // Section collapse/expand indicator. The element carries the shipped look inline (see
        // StorefrontSectionEditor); this rule is the merchant's override on top, matching how the
        // live page merges sectionToggle over the same defaults.
        // Title text block inside the header band (homePageConfig.sectionTitleText). Colour is
        // intentionally absent — it inherits from the per-section band, same as on the storefront.
        ['.sf-home-style-scope .storefront-section-title-text', elementStyleFrom(homeConfig.sectionTitleText)],
        ['.sf-home-style-scope .storefront-section-toggle', elementStyleFrom(homeConfig.sectionToggle)],
        // Hover is keyed off the HEADER, exactly as the storefront emits it: the whole band is the
        // toggle there, and the indicator is pointer-events:none, so it never hovers on its own. In
        // the preview the header is an editing surface rather than a toggle, but keying the rule the
        // same way is what lets the merchant SEE their hover styling by pointing at the header.
        ...(Object.keys(homeSectionToggleHoverStyle).length ? ([
            ['.sf-home-style-scope .storefront-section-toggle', hoverTransition],
            ['.sf-home-style-scope h3:hover .storefront-section-toggle', homeSectionToggleHoverStyle],
        ] as [string, React.CSSProperties][]) : []),
        // Per-section "More like this" link. The element carries the shipped base look inline (see
        // StorefrontSectionEditor); this is the merchant's override on top, exactly as the live page
        // spreads moreLikeThisButton over .more-like-this-button.
        ['.sf-home-style-scope .storefront-more-like-this', elementStyleFrom(homeConfig.moreLikeThisButton)],
        ['.sf-home-style-scope .storefront-category-pod-button', elementStyleFrom(homeConfig.pod)],
        ['.sf-home-style-scope .storefront-category-pod-name', textStyleFrom(homeConfig.podName)],
        ['.sf-home-style-scope .storefront-category-container', containerStyleFrom(homeConfig.podContainer)],
        ...(Object.keys(homePodHoverStyle).length ? ([
            ['.sf-home-style-scope .storefront-category-pod-button', hoverTransition],
            ['.sf-home-style-scope .storefront-category-pod-button:hover', homePodHoverStyle],
        ] as [string, React.CSSProperties][]) : []),
        ['.sf-home-style-scope .storefront-nav-pod-button', elementStyleFrom(homeConfig.navPod)],
        ['.sf-home-style-scope .storefront-nav-pod-name', textStyleFrom(homeConfig.navPodName)],
        ['.sf-home-style-scope .storefront-nav-pod-image', elementStyleFrom(homeConfig.navPodImage)],
        ['.sf-home-style-scope .storefront-nav-pod-link', textStyleFrom(homeConfig.navPodLink)],
        ['.sf-home-style-scope .storefront-nav-pod-container', containerStyleFrom(homeConfig.navPodContainer)],
        ...(Object.keys(homeNavPodHoverStyle).length ? ([
            ['.sf-home-style-scope .storefront-nav-pod-button', hoverTransition],
            ['.sf-home-style-scope .storefront-nav-pod-button:hover', homeNavPodHoverStyle],
        ] as [string, React.CSSProperties][]) : []),
        // Nav-pod SVG icons are CSS masks, so their tint IS a background color. These two rules come
        // AFTER the .storefront-nav-pod-image rule above deliberately: same specificity, same
        // !important, so the dedicated icon color wins over a background set on the image dict.
        ...(homeConfig.navPodIconColor ? ([
            ['.sf-home-style-scope .storefront-nav-pod-icon', { backgroundColor: homeConfig.navPodIconColor }],
        ] as [string, React.CSSProperties][]) : []),
        ...(homeConfig.navPodIconHoverColor ? ([
            ['.sf-home-style-scope .storefront-nav-pod-button:hover .storefront-nav-pod-icon', { backgroundColor: homeConfig.navPodIconHoverColor }],
        ] as [string, React.CSSProperties][]) : []),
        // homeBlockGap: each stacked block's own source margin is zeroed so the uniform rowGap
        // (applied on .storefront-body below) is the ONLY gap between blocks — mirrors HomePage.tsx
        // zeroing home-message/nav-pod/category margins at their source. Unset = untouched, as today.
        ...(homeBlockGap !== undefined ? ([
            ['.sf-home-style-scope .storefront-banner-container', { marginBottom: 0 }],
            ['.sf-home-style-scope .storefront-category-container', { marginTop: 0 }],
            ['.sf-home-style-scope .storefront-nav-pod-container', { margin: 0 }],
            ['.sf-home-style-scope .storefront-sections-wrapper', { marginTop: 0 }],
        ] as [string, React.CSSProperties][]) : []),
    ] as [string, React.CSSProperties][])
        .map(([sel, style]) => { const body = cssText(style); return body ? `${sel} { ${body} }` : ''; })
        .filter(Boolean)
        .join('\n');

    // Affiliations / endorsements / awards band preview — a labeled row of logo pods. Rendered
    // inline (rather than via the scoped stylesheet) since the image style needs a fallback default
    // when unset, and the label may sit above/left/below the row. Placement (top/bottom of page) is
    // decided by the caller; this just renders the band itself.
    const renderAffiliationsBand = () => {
        const labelNode = affiliations?.label && affiliationsLabelPosition !== 'none'
            ? <div style={affiliationsLabelStyle}>{affiliations.label}</div>
            : null;
        const rowNode = (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: affiliationsJustify, gap: affiliationsGap }}>
                {affiliationsItems.map((item) => {
                    const img = <img src={item.imageUrl} alt={item.altText ?? ''} style={affiliationsImageStyle} />;
                    return (
                        <div key={item.id} style={affiliationsPodStyle}>
                            {item.linkUrl ? <a href={item.linkUrl} target='_blank' rel='noreferrer noopener'>{img}</a> : img}
                        </div>
                    );
                })}
            </div>
        );
        return (
            <div style={affiliationsBandStyle} className={props.editMode ? 'sf-pencil-host' : undefined}>
                {props.editMode && <EditPencil onClick={() => openAffiliations('style')} title='Edit affiliations style' />}
                <div style={{
                    display: 'flex',
                    flexDirection: affiliationsLabelPosition === 'left' ? 'row' : 'column',
                    alignItems: affiliationsLabelPosition === 'left' ? 'center' : undefined,
                    gap: 12,
                }}>
                    {affiliationsLabelPosition !== 'bottom' && labelNode}
                    {rowNode}
                    {affiliationsLabelPosition === 'bottom' && labelNode}
                </div>
            </div>
        );
    };

    // The bottom "Browse All" area, in whichever of its two shapes the config asks for: the classic
    // centered button (no browseAllBand) or the full-bleed CTA band with the button inside it. Both
    // are drawn exactly as the live HomePage.tsx draws them (same .browse-all-button class, same
    // band composition via browseAllBandStyle / BrowseAllBandContent), so this is a like-for-like
    // preview rather than an approximation.
    //
    // Rendered in preview mode as well as edit mode: the live store always renders Browse All on the
    // main homepage, and it is the shape of the page's ending.
    const renderBrowseAll = () => {
        // One wrapper for both shapes so the pencil / click-to-edit wiring is written once and the
        // style is the only thing that switches.
        const bandStyle: React.CSSProperties = bab
            // fullBleedFromColor paints the band's solid color edge to edge across the editor canvas
            // (the box-shadow/clip-path trick the affiliations band uses). Spread AFTER the band
            // style so its backgroundColor/boxShadow/clipPath compose on top. A background IMAGE
            // still stops at the body width: the accepted canvas tradeoff, since the trick can only
            // extend a flat color.
            ? { ...browseAllBandStyle(bab), ...(bab.backgroundColor ? fullBleedFromColor(bab.backgroundColor) : {}) }
            : { paddingTop: '2em', display: 'flex', justifyContent: 'center' };
        return (
            <div
                className={props.editMode ? 'sf-pencil-host' : undefined}
                style={{ ...bandStyle, ...(props.editMode ? { cursor: 'pointer' } : {}) }}
                title={props.editMode ? 'Edit the Browse All area' : undefined}
                onClick={props.editMode ? () => setOpenPanel('browseAllBand') : undefined}
            >
                {props.editMode && <EditPencil onClick={() => setOpenPanel('browseAllBand')} title='Edit Browse All' />}
                {bab
                    ? <BrowseAllBandContent band={bab} buttonText={browseAllText} isDark={isDark} />
                    // No band: the plain button with no inline style, since the live store only
                    // applies buttonStyle when a band is configured.
                    : <button type='button' className={browseAllButtonClass(isDark)}>{browseAllText}</button>}
            </div>
        );
    };

    // Header preview (read-only here — edited in the Header & Footer tab). In bannerUnderHeader mode
    // it's absolutely positioned to overlay the top of the preview (which .storefront-editor anchors
    // via its position:relative) and rendered transparent (overlayHero); otherwise it renders bare in
    // normal flow, byte-identical to today.
    const headerEditor = (
        <StorefrontHeaderEditor
            storefrontSettings={props.storefrontSettings}
            storefrontTheme={props.storefrontTheme}
            globalSettings={props.globalSettings}
            globalTheme={props.globalTheme}
            onStorefrontSettingsChange={props.onStorefrontSettingsChange}
            editMode={false}
            storefrontLinks={props.storefrontHeaderLinks}
            globalLinks={props.globalHeaderLinks}
            onLinksChange={props.onHeaderLinksChange}
            overlayHero={bannerUnderHeader}
            overlayBackground={heroOptions.bannerUnderHeaderBackground}
            overlayShadow={heroOptions.bannerUnderHeaderShadow}
        />
    );

    // Page-title frame. Normally the first block above the banner; in bannerUnderHeader mode the
    // hero must be the first thing under the overlaid header, so it renders below the banner
    // instead (mirrors HomePage.tsx).
    const titleFrame = (
        <div style={bannerFullWidth ? contentFrameStyle : undefined}>
        <h1
            className='storefront-title'
            style={{ ...pageTitlePreviewStyle, ...(homeContentPaddingTop !== undefined ? { marginTop: 0, marginBottom: 0 } : {}) }}
        >
            <StorefrontTextEditor
                value={props.storefrontSettings.title?.trim()}
                notSetValue={props.globalSettings.title?.trim() || 'Storefront Title'}
                onChange={(title) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, title })}
                editable={props.editMode}
                style={{
                    fontFamily: headerFont,
                    color: textColor,
                }}
            />
        </h1>
        </div>
    );

    // The Home Page Style bar's seven entries. Each is one button, one panel; `checked` renders
    // the pressed highlight while that entry's panel is open. (This replaced the labeled-box
    // layout of one Label + DefaultButton per entry after a side-by-side trial.)
    const styleBarItems: ICommandBarItemProps[] = [
        { key: 'banners', text: 'Banners', iconProps: { iconName: 'Slideshow' }, checked: openPanel === 'banners', onClick: () => { setOpenPanel('banners'); setBannerPanelRequest(true); } },
        // The page-level way into a section. Every other entry here opens ONE panel of page-wide
        // styling; this one is a picker, because section styling is per section — the bar entry a
        // merchant looks for is "sections", and what they want next is the section they mean.
        { key: 'sections', text: 'Sections', title: 'Every section on this page — reorder, add, delete, or open one.', iconProps: { iconName: 'CustomList' }, checked: openPanel === 'sections', onClick: () => { setOpenPanel('sections'); setSectionsPanelRequest(true); } },
        { key: 'cards', text: 'Product Cards', iconProps: { iconName: 'GridViewMedium' }, checked: openPanel === 'card', onClick: () => setOpenPanel('card') },
        { key: 'pods', text: 'Category Pods', iconProps: { iconName: 'GridViewMedium' }, checked: openPanel === 'pod', onClick: () => setOpenPanel('pod') },
        { key: 'navPods', text: 'Nav Pods', iconProps: { iconName: 'Nav2DMapView' }, checked: navPodsOpen, onClick: () => openNavPods('pods') },
        { key: 'affiliations', text: 'Affiliations / Awards', iconProps: { iconName: 'Trophy2' }, checked: affiliationsOpen, onClick: () => openAffiliations('logos') },
        { key: 'spinner', text: 'Loading Spinner', iconProps: { iconName: 'ProgressRingDots' }, checked: openPanel === 'spinner', onClick: () => setOpenPanel('spinner') },
        { key: 'browseAll', text: 'Browse All', iconProps: { iconName: 'CallToAction' }, checked: openPanel === 'browseAllBand', onClick: () => setOpenPanel('browseAllBand') },
        { key: 'suggestions', text: 'Search Suggestions', title: 'Style the suggestion panel that drops below the home page search bar.', iconProps: { iconName: 'Color' }, checked: openPanel === 'suggestions', onClick: () => setOpenPanel('suggestions') },
    ];

    return (<div>
        <StorefrontAutocompleteStylePanel
            context='homePage'
            isOpen={openPanel === 'suggestions'}
            onDismiss={closePanel}
            storefrontSettings={props.storefrontSettings}
            onStorefrontSettingsChange={props.onStorefrontSettingsChange}
        />
        {/* Scoped stylesheet applying homePageConfig to the preview's cards + pods. */}
        {homeStyleRules && <style>{homeStyleRules}</style>}
        {/* The Home Page Style bar (banner/cards/pods/nav-pods/affiliations/spinner) is main-homepage
            presentation — hide it on custom pages, which are banners + sections only. */}
        {!isCustomPage && props.editMode && (
            <RawJsonEditor
                label='Home Page Style'
                liveObject={homeConfig}
                onApply={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, homePageConfig: json })}
                keysHint='Keys: banner, bannerFullWidth, bannerImageFit, bannerUnderHeader, bannerText, card, cardHover, title, price, image, pod, podName, podContainer, podHover, navPods, navPod, navPodName, navPodImage, navPodLink, navPodContainer, navPodHover, navPodIconColor, navPodIconHoverColor, navPodScroll, navPodAlign, scrollButton, scrollbar, affiliations, browseAllBand. cardHover, podHover and navPodHover are emitted as scoped :hover rules on the product cards, category pods and nav pods (any CSS, e.g. boxShadow or transform). navPodIconColor tints a nav pod recolorable SVG icon (iconSvg), and navPodIconHoverColor recolors it on hover; uploaded images keep their own colors. bannerFullWidth (boolean) renders the banner across the full width with page content kept at maxWidth; bannerImageFit is fit (letterbox) | fill (crop); bannerUnderHeader (boolean) floats a transparent nav header over the top of the full-bleed hero (requires bannerFullWidth; desktop only on the live store); bannerUnderHeaderBackground (CSS color, e.g. rgba(0,0,0,0.35)) tints that floating bar before the shopper scrolls so the header text stays legible; bannerUnderHeaderShadow (CSS box-shadow) softens the floating bar edge in the same not-scrolled state. navPod width/height set the pod dimensions; navPodScroll (boolean) makes the pod row a horizontal scroller instead of wrapping; navPodAlign is left | center | right | spaceBetween. scrollButton styles both side-scroll arrow buttons; scrollbar styles the scrolling row (e.g. { scrollbarColor: "#ff9900 #000", scrollbarWidth: "thin" }). affiliations is { items: [{id, imageUrl, altText, linkUrl}], label, description, labelPosition: top|left|bottom|none, position: top|bottom, align: left|center|right|spaceBetween, gap, band, pod, image, labelStyle, descriptionStyle } — a row of partner/award logos; descriptionStyle styles the blurb rendered under the label. browseAllBand is { heading, subheading, backgroundColor, backgroundImageUrl, overlayColor, overlayOpacity (0-100), fixed (parallax boolean), minHeight, headingStyle, subheadingStyle, buttonStyle } — renders the bottom Browse All area as a full-bleed call-to-action band with the button inside it; absent = the classic centered button. Any CSS property (camelCase, e.g. boxShadow) works; numbers become px — use a string for other units. fontSize is a percent. url(), @import, javascript: and angle brackets are blocked. Changes apply while the JSON is valid.'
            >
                {/* One button, one panel per entry. The banner button's five old hero settings
                    live in the banner panel's Carousel Settings group now (they were a straight
                    duplicate); overflow items collapse into the bar's ellipsis menu on narrow
                    windows. */}
                <CommandBar
                    items={styleBarItems}
                    styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                />
            </RawJsonEditor>
        )}
        {/* Product-card style panel — tile / name / price / image / hover on one flat surface,
            shared with the Search page editor. cardHover is wired: the live HomePage emits it as a
            scoped :hover rule on the cards, and the preview stylesheet above mirrors that. */}
        <ProductCardStylePanel
            isOpen={openPanel === 'card'}
            onDismiss={closePanel}
            modeNoun='Card'
            configField='homePageConfig'
            card={homeConfig.card}
            onCardChange={updateCard}
            title={homeConfig.title}
            onTitleChange={updateTitle}
            price={homeConfig.price}
            onPriceChange={updatePrice}
            image={homeConfig.image}
            onImageChange={updateImage}
            cardHover={homeConfig.cardHover}
            onCardHoverChange={updateCardHover}
            isDark={isDark}
        />
        {/* Loading spinner style panel: ring + label rows with a live look-alike of the
            storefront's Fluent v9 spinner pinned above them (see SpinnerStylePanel). */}
        <SpinnerStylePanel
            isOpen={openPanel === 'spinner'}
            onDismiss={closePanel}
            value={spinnerValue(props.storefrontSettings)}
            onChange={(patch) => props.onStorefrontSettingsChange(setSpinner(props.storefrontSettings, patch))}
            labelText={loadingText}
            isDark={isDark}
        />
        {/* Category pod style panel: pod / label / container / hover on one flat surface with a
            live example pinned above it (mirrors the product card panel). */}
        <CategoryPodStylePanel
            isOpen={openPanel === 'pod'}
            onDismiss={closePanel}
            pod={homeConfig.pod}
            onPodChange={updatePod}
            podName={homeConfig.podName}
            onPodNameChange={updatePodName}
            podContainer={homeConfig.podContainer}
            onPodContainerChange={updatePodContainer}
            podHover={homeConfig.podHover}
            onPodHoverChange={updatePodHover}
            isDark={isDark}
        />
        {/* Nav pods panel: the pod list and the nav-pod styling, one tab each. initialFocusPodId
            drives the canvas click-to-edit / ghost-add-tile flow: the panel expands + scrolls to
            that pod once, then calls onInitialFocusHandled to clear it. initialPivot does the same
            for the tab, so the band's pencil lands on Style and a pod's click lands on Pods. */}
        <StorefrontNavPodsEditor
            isOpen={navPodsOpen}
            onDismiss={() => navPodsPanel.close()}
            storefrontId={props.storefrontSettings.id ?? ''}
            value={homeConfig.navPods}
            onChange={updateNavPods}
            initialFocusPodId={navPodFocusId}
            onInitialFocusHandled={() => setNavPodFocusId(undefined)}
            // A link can name the pivot too (?panelTab=style), since the panel's Style tab holds the
            // shared pod styling. A click still wins: openNavPods sets this state explicitly.
            initialPivot={navPodsPivot ?? (navPodsPanel.panelTab as 'pods' | 'style' | undefined)}
            onInitialPivotHandled={() => setNavPodsPivot(undefined)}
            config={homeConfig}
            onConfigChange={updateConfig}
            isDark={isDark}
        />
        {/* Browse All panel: the enable toggle plus the band's text, background, heading,
            subheading and button on one flat surface with a live example pinned above it
            (mirrors the product card and category pod panels). */}
        <BrowseAllStylePanel
            isOpen={openPanel === 'browseAllBand'}
            onDismiss={closePanel}
            value={bab}
            onChange={(band) => updateConfig({ browseAllBand: band })}
            buttonText={browseAllText}
            isDark={isDark}
        />
        {/* Affiliations panel: the logo list and the band styling, one tab each. The band's canvas
            pencil deep-links to Style (initialPivot); the style bar button opens Logos. */}
        <StorefrontAffiliationsEditor
            isOpen={affiliationsOpen}
            onDismiss={() => affiliationsPanel.close()}
            storefrontId={props.storefrontSettings.id ?? ''}
            value={homeConfig.affiliations}
            onChange={(v) => updateConfig({ affiliations: v })}
            initialPivot={affiliationsPivot}
            onInitialPivotHandled={() => setAffiliationsPivot(undefined)}
            isDark={isDark}
        />
        {/* The storefront preview lives in its own .storefront-editor wrapper carrying the
            storefront's (dark/light) background, so the Home Page Style controls above sit
            on the neutral editor background (mirrors the Search page editor). */}
        <PreviewCanvas style={{ backgroundColor }}>
            {props.editMode && (
                <StorefrontColorPickerButton
                    label='Page Style'
                    entries={[
                        {
                            type: 'color',
                            label: 'Background',
                            settingKey: 'storefront.style.backgroundColor',
                            allowPaletteTokens: true,
                            value: styleValue(props.storefrontSettings, 'backgroundColor'),
                            inheritedValue: props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor,
                            onChange: (color) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { backgroundColor: color })),
                            onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { backgroundColor: '' })),
                        },
                        {
                            type: 'color',
                            label: 'Text',
                            settingKey: 'storefront.style.textColor',
                            allowPaletteTokens: true,
                            value: styleValue(props.storefrontSettings, 'textColor'),
                            inheritedValue: props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor,
                            onChange: (color) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { textColor: color })),
                            onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { textColor: '' })),
                        },
                        {
                            type: 'font',
                            label: 'Header Font',
                            settingKey: 'storefront.style.headerFont',
                            allowPaletteTokens: true,
                            value: styleValue(props.storefrontSettings, 'headerFont'),
                            inheritedValue: props.globalSettings.headerFont,
                            onChange: (font) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { headerFont: font })),
                            onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { headerFont: '' })),
                        },
                        {
                            type: 'font',
                            label: 'Body Font',
                            settingKey: 'storefront.style.defaultFont',
                            allowPaletteTokens: true,
                            value: styleValue(props.storefrontSettings, 'defaultFont'),
                            inheritedValue: props.globalSettings.defaultFont,
                            onChange: (font) => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { defaultFont: font })),
                            onClear: () => props.onStorefrontSettingsChange(setStyle(props.storefrontSettings, { defaultFont: '' })),
                        },
                    ]}
                    style={{ position: 'absolute', top: 58, right: 10, zIndex: 10 }}
                />
            )}
        {/* Header/footer are edited only in the dedicated Header & Footer tab; shown
            here read-only for page context. In bannerUnderHeader mode the header is lifted out of
            normal flow and overlaid at the top so the hero banner shows through behind it. */}
        {bannerUnderHeader
            ? <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>{headerEditor}</div>
            : headerEditor}
        {/* When bannerFullWidth is on, the scope-carrying body spans the full preview width and
            the banner is a full-bleed child; the title and all other content are wrapped in a
            centered page-frame (contentFrameStyle) so they stay at the page maxWidth. The
            sf-home-style-scope class stays on this body (an ancestor of everything it styles). */}
        <div
            className='storefront-body sf-home-style-scope'
            style={{
                // When full-bleed, the body spans the full preview width and content is re-centered
                // by contentFrameStyle on the inner frames; otherwise the body itself holds maxWidth.
                ...(bannerFullWidth
                    ? { width: '100%', maxWidth: 'none', padding: 0 }
                    : { maxWidth: `${pageMaxWidth}px` }),
                color: textColor,
                fontFamily: defaultFont,
                // homeContentPaddingTop: applied on the content wrapper (negative values pull the
                // first block up under the header, matching HomePage.tsx's outer container).
                ...(homeContentPaddingTop !== undefined ? { marginTop: homeContentPaddingTop } : {}),
                // homeBlockGap: uniform vertical gap between every stacked block below (title through
                // sections). Each block's own source margin is zeroed above (homeStyleRules) / inline
                // below so this rowGap is the only spacing. Unset = today's plain block stack, unchanged.
                ...(homeBlockGap !== undefined ? { display: 'flex', flexDirection: 'column', rowGap: homeBlockGap } : {}),
                // bannerUnderHeader: pull the body to the preview's top edge so the hero starts under
                // the overlaid header. Only the top margin is zeroed (wins over the .storefront-body
                // 22px and any homeContentPaddingTop above); horizontal/bottom margins are untouched.
                ...(bannerUnderHeader ? { marginTop: 0 } : {}),
                // A configured Browse All band is the last thing on the live page and sits flush
                // against the footer (HomePage.tsx zeroes #content's bottom padding), so zero the
                // body's 22px bottom margin the same way. Classic button keeps today's gap.
                ...(!isCustomPage && bab ? { marginBottom: 0 } : {}),
            }}
            >
            {/* The store title is a shared, main-homepage heading — hidden on custom pages (which have
                their own title in pagesConfig and are banners + sections only). */}
            {!isCustomPage && !bannerUnderHeader && titleFrame}
            <div className='storefront-home-banner'>
                <StorefrontBannerEditor
                    banners={props.banners}
                    onChange={props.onBannersChange}
                    page={props.selectedPageId}
                    pages={props.pages}
                    editable={props.editMode}
                    // Custom pages use their own per-page banner height (pagesConfig.bannerHeight);
                    // the main homepage uses the storefront/global banner height.
                    height={isCustomPage
                        ? (currentPage?.bannerHeight ?? undefined)
                        : (props.storefrontSettings.bannerHeight ?? props.globalSettings.bannerHeight)}
                    onHeightChange={(height) => isCustomPage
                        ? updateCurrentPage({ bannerHeight: height })
                        : props.onStorefrontSettingsChange({...props.storefrontSettings, bannerHeight: height})}
                    width={bannerFullWidth ? undefined : maxWidthPx}
                    objectFit={bannerFullWidth ? 'cover' : undefined}
                    dotsBelow={homeConfig.bannerDotsBelow === true}
                    autoHeight={bannerAutoHeight}
                    overlayScale={bannerOverlayScale}
                    // The "Ignore Global Banners" toggle applies only to the main homepage context.
                    ignoreGlobalBanners={isCustomPage ? undefined : props.storefrontSettings.ignoreGlobalBanners}
                    onIgnoreGlobalBannersChange={isCustomPage ? undefined : ((v) => props.onStorefrontSettingsChange({...props.storefrontSettings, ignoreGlobalBanners: v}))}
                    imageFit={heroOptions.bannerImageFit}
                    contentMaxWidth={bannerFullWidth ? pageMaxWidth : undefined}
                    heroOptions={heroOptions}
                    onHeroOptionsChange={props.editMode ? updateHeroOptions : undefined}
                    // Dwell seconds per slide (homePageConfig.bannerRotationSeconds). Always read off
                    // homeConfig — like bannerAutoHeight it is store-wide, not per-page — so the
                    // preview paces every page's carousel at the live rate; edited from the main
                    // homepage only, where "this changes the whole store" is what the panel says.
                    rotationSeconds={homeConfig.bannerRotationSeconds}
                    onRotationSecondsChange={props.editMode && !isCustomPage
                        ? ((seconds) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, homePageConfig: serializeHomePageConfig({ ...homeConfig, bannerRotationSeconds: seconds }) }))
                        : undefined}
                    openEditPanelRequest={bannerPanelRequest}
                    onOpenEditPanelHandled={() => setBannerPanelRequest(false)}
                    panelActive={openPanel === 'banners'}
                    onEditPanelOpenChange={reportChildPanel(stylePanels.banners)}
                />
            </div>
            {!isCustomPage && bannerUnderHeader && titleFrame}
            {/* Affiliations / endorsements / awards band — 'top' placement: right after the banner,
                before the banner-text/nav-pod band. Rendered full-bleed, outside the content frame.
                Main-homepage only. */}
            {!isCustomPage && affiliationsVisible && (affiliations?.position ?? 'bottom') === 'top' && renderAffiliationsBand()}
            <div style={bannerFullWidth ? contentFrameStyle : undefined}>
            {/* Mirror live HomePage.tsx, which renders the banner-message block only when bannerText
                is set. In edit mode we still render it (an editable placeholder box), but in preview
                with empty text it's omitted entirely — otherwise an empty padded/bordered box sits
                between the banner and the nav pods as a spurious gap. Main-homepage only. */}
            {!isCustomPage && (props.editMode || !!props.storefrontSettings.bannerText?.trim()) && <StorefrontTextEditor
                value={props.storefrontSettings.bannerText?.trim()}
                notSetValue={props.globalSettings.bannerText?.trim() || 'Banner Text'}
                editable={props.editMode}
                onChange={(bannerText) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, bannerText })}
                rootClassName={`storefront-home-message${isDark ? ' storefront-home-message-dark' : ''}`}
                style={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#eee',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#ddd'}`,
                    borderRadius: 3,
                    color: isDark ? 'inherit' : '#333',
                    padding: '1em',
                    // fontFamily: props.storefrontSettings.defaultFont ?? props.globalSettings.defaultFont,
                    // color: props.storefrontSettings.textColor || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#000',
                }}
            />}
            {!isCustomPage && !props.storefrontSettings.useHeaderSearch && <StorefrontSearchBarEditor
                context='homePage'
                onEditStyles={() => setOpenPanel('suggestions')}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                storefrontSettings={props.storefrontSettings}
                editMode={props.editMode}
                style={{marginTop: homeBlockGap !== undefined ? 0 : '1em' }}
                isDark={isDark}
            />}
            {/* Edit mode renders the band even with zero pods (just the ghost add tile) so there's
                always somewhere to click to get started; preview mode stays hidden when empty,
                mirroring the live store. */}
            {!isCustomPage && ((homeConfig.navPods?.length ?? 0) > 0 || props.editMode) && (
                <div style={navPodBandStyle}>
                    <div
                        className={`storefront-nav-pod-container${homeConfig.navPodScroll ? ' storefront-nav-pod-scroll' : ''}${props.editMode ? ' sf-pencil-host' : ''}`}
                        // A scrolling pod row needs different justification from a wrapping one: plain
                        // center/flex-end clip the overflow at the start instead of scrolling from it.
                        style={{ justifyContent: homeConfig.navPodScroll ? navPodScrollJustify(homeConfig.navPodAlign) : navPodJustify(homeConfig.navPodAlign) }}
                    >
                        {props.editMode && <EditPencil onClick={() => openNavPods('style')} title='Edit nav pod style' />}
                        {homeConfig.navPods?.map((pod) => (
                            <div
                                key={pod.id}
                                className={`storefront-nav-pod-button${isDark ? ' storefront-nav-pod-button-dark' : ''}`}
                                style={props.editMode ? { cursor: 'pointer' } : undefined}
                                title={props.editMode ? 'Edit this nav pod' : undefined}
                                onClick={props.editMode ? () => { setNavPodFocusId(pod.id); openNavPods('pods'); } : undefined}
                            >
                                {/* Same precedence as the live store: a recolorable SVG icon wins
                                    over an uploaded image. The icon is a CSS mask, so its tint is a
                                    background color, applied (with its hover swap) by the scoped
                                    stylesheet above rather than inline. */}
                                {/* No invert on a dark background. The storefront's dark theme inverts
                                    `.category-pod-button img` and nothing else (public/themes/custom_dark.css),
                                    so CATEGORY pod art flips there but NAV pod art never does. The preview
                                    used to invert both, which turned every uploaded nav-pod image into its own
                                    negative on any dark store — the pods looked right on the live site and
                                    wrong here. Nav-pod image styling comes from navPodImage via the scoped
                                    stylesheet, same as the live page's inline homeNavPodImageStyle. */}
                                {pod.iconSvg
                                    ? <span className='storefront-nav-pod-image storefront-nav-pod-icon' style={svgMaskStyle(pod.iconSvg, 64)} />
                                    : pod.imageUrl
                                        ? <img className='storefront-nav-pod-image' src={pod.imageUrl} alt={pod.name ?? ''} />
                                        : null}
                                {pod.name && <div className='storefront-nav-pod-name'>{pod.name}</div>}
                                {pod.linkText && <div className='storefront-nav-pod-link'>{pod.linkText}</div>}
                            </div>
                        ))}
                        {/* Ghost tile — always last, edit mode only. Mints a new pod up front (rather
                            than waiting on the panel) so the click has an immediate visible result in
                            the canvas, then hands off to the panel focused on it. */}
                        {props.editMode && (
                            <div
                                className={`storefront-nav-pod-add${isDark ? ' storefront-nav-pod-add-dark' : ''}`}
                                title='Add a nav pod'
                                onClick={() => {
                                    const newPod: INavPod = { id: genId() };
                                    updateNavPods([...(homeConfig.navPods ?? []), newPod]);
                                    setNavPodFocusId(newPod.id);
                                    openNavPods('pods');
                                }}
                            >
                                <Icon iconName='Add' />
                                <span>Add nav pod</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Category pods are a main-homepage feature — hidden on custom pages. */}
            {!isCustomPage && <StorefrontCategoryEditor
                globalSettings={props.globalSettings}
                storefrontSettings={props.storefrontSettings}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                editMode={props.editMode}
                subUrl={props.storefrontSettings.subUrl}
                isDark={isDark}
                onEditStyle={props.editMode ? () => setOpenPanel('pod') : undefined}
            />}
            </div>
            {/* Sections sit OUTSIDE the centered content frame so a section band can paint edge to
                edge across the canvas, the way the live page breaks each band out to 100vw. The
                frame is not lost — it is handed to the sections editor as `contentFrameStyle` and
                re-applied INSIDE each band, which is exactly the shopper renderer's
                fullBleedBand(..., constrainInner: true): full-width band, content re-centered at the
                page max width. Wrapping the sections in the frame out here instead is what capped
                every band at the content column while the live ones spanned the viewport. */}
            {/* The two card details that come from the PAGE rather than from any section: the
                add-to-cart button's styling and whether the favorite heart shows at all. The live
                HomePage resolves both once and hands them to every card; the provider is the
                preview's equivalent, since the cards sit several preview flavors deep. */}
            <StorefrontCardChromeProvider
                addToCartStyle={homeAddToCartStyle}
                showFavorites={props.storefrontSettings.enableProductFavorites}
                scrollButtonStyle={homeScrollButtonStyle}
                scrollbarStyle={homeScrollbarStyle}
            >
            <StorefrontSectionsEditor
                storefrontSettings={props.storefrontSettings}
                editMode={props.editMode}
                sections={props.sections}
                onSectionsChange={props.onSectionsChange}
                onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                isDark={isDark}
                page={props.selectedPageId}
                openPanelRequest={sectionsPanelRequest}
                onOpenPanelHandled={() => setSectionsPanelRequest(false)}
                panelActive={openPanel === 'sections'}
                onPanelOpenChange={reportChildPanel(stylePanels.sections)}
                // Section bands are a main-homepage styling feature; custom pages get none.
                sectionBands={isCustomPage ? undefined : homeConfig.sectionBands}
                sectionToggleIcons={sectionToggleIcons}
                moreLikeThisLabel={moreLikeThisLabel}
                // The card popover's "Page-wide card style…" opens the homePageConfig panel — a
                // shared blob whose edits also restyle the MAIN homepage, so like the Home Page
                // Style bar it's offered on the main homepage only (custom pages are banners +
                // sections; their cards still take the section layer from the popover itself).
                onOpenPageCardStyle={props.editMode && !isCustomPage ? () => setOpenPanel('card') : undefined}
                // The centered frame each band re-applies to its own contents. Only in full-width
                // mode: otherwise the body itself already holds the max width and the sections are
                // centered by it, so a second frame would inset them twice.
                contentFrameStyle={bannerFullWidth ? contentFrameStyle : undefined}
                />
            </StorefrontCardChromeProvider>
            {/* Affiliations / endorsements / awards band — 'bottom' (default) placement: last thing
                before the footer editor. Rendered full-bleed, outside the content frame. Main-homepage only. */}
            {!isCustomPage && affiliationsVisible && (affiliations?.position ?? 'bottom') === 'bottom' && renderAffiliationsBand()}
            {/* Browse All. Placement mirrors HomePage.tsx: the last block on the page, after the
                bottom affiliations band and before the footer. Main-homepage only, because the live
                custom-page branch renders banners + sections and nothing else. */}
            {!isCustomPage && renderBrowseAll()}
        </div>
        <StorefrontFooterEditor
            storefrontSettings={props.storefrontSettings}
            storefrontTheme={props.storefrontTheme}
            globalSettings={props.globalSettings}
            globalTheme={props.globalTheme}
            onStorefrontSettingsChange={props.onStorefrontSettingsChange}
            editMode={false}
            footerLinks={props.storefrontFooterLinks}
            globalFooterLinks={props.globalFooterLinks}
         />
        </PreviewCanvas>
    </div>)
}

export default StorefrontHomePageEditor;